import React from 'react';
import { RaakvlakAnalyse, HINDER_TYPE_LABELS, HinderType } from '../../types';
import styles from './GebiedsanalyseModule.module.css';

interface RaakvlakprojectenSectionProps {
  data: RaakvlakAnalyse | null;
  loading?: boolean;
  onItemClick?: (lat: number, lng: number) => void;
}

const NIVEAU_COLORS: Record<string, string> = {
  hoog: '#ef4444',
  gemiddeld: '#f59e0b',
  laag: '#22c55e',
};

const BRON_COLORS: Record<string, string> = {
  NDW: '#f97316',
  'Overheid.nl': '#10b981',
  Intern: '#E6007E',
};

const RaakvlakprojectenSection: React.FC<RaakvlakprojectenSectionProps> = ({ data, loading = false, onItemClick }) => {
  if (loading) {
    return (
      <div className={styles.section}>
        <h3>Raakvlakprojecten</h3>
        <div className={styles.invloedsgebiedLoading}>
          <div className={styles.spinnerSmall} />
          <span>Raakvlakprojecten detecteren...</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { projecten, hinderAnalyse, cumulatieveImpact, communicatieRisicos } = data;

  return (
    <div className={styles.section}>
      <h3>Raakvlakprojecten in de omgeving</h3>

      {/* Impact score card */}
      <div className={styles.raakvlakImpactCard} style={{ borderLeftColor: NIVEAU_COLORS[cumulatieveImpact.niveau] }}>
        <div className={styles.raakvlakImpactHeader}>
          <div className={styles.raakvlakImpactScore}>
            <span className={styles.raakvlakScoreValue} style={{ color: NIVEAU_COLORS[cumulatieveImpact.niveau] }}>
              {cumulatieveImpact.score}
            </span>
            <span className={styles.raakvlakScoreMax}>/10</span>
          </div>
          <div>
            <span className={styles.raakvlakNiveau} style={{ color: NIVEAU_COLORS[cumulatieveImpact.niveau] }}>
              {cumulatieveImpact.niveau.charAt(0).toUpperCase() + cumulatieveImpact.niveau.slice(1)} risico
            </span>
            <span className={styles.raakvlakTotaal}>
              {data.totaalRaakvlakprojecten} project{data.totaalRaakvlakprojecten !== 1 ? 'en' : ''} binnen {data.zoekRadius}m
            </span>
          </div>
        </div>
        <p className={styles.raakvlakSamenvatting}>{cumulatieveImpact.samenvatting}</p>
      </div>

      {/* Hinder type totalen */}
      {hinderAnalyse.hinderTypeTotaal.length > 0 && (
        <div className={styles.raakvlakHinderTypes}>
          {hinderAnalyse.hinderTypeTotaal.map(ht => (
            <span key={ht.type} className={styles.raakvlakHinderTag}>
              {ht.label} ({ht.count})
            </span>
          ))}
        </div>
      )}

      {/* Overlappende hinder */}
      {hinderAnalyse.overlaps.length > 0 && (
        <div className={styles.raakvlakOverlaps}>
          <h4>Overlappende hinder ({hinderAnalyse.aantalOverlaps})</h4>
          {hinderAnalyse.overlaps.slice(0, 3).map((overlap, i) => (
            <div key={i} className={styles.raakvlakOverlapCard}>
              <div className={styles.raakvlakOverlapProjects}>
                <span>{overlap.projectA.naam}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="2">
                  <path d="M18 8l4 4-4 4" /><path d="M6 16l-4-4 4-4" /><line x1="2" y1="12" x2="22" y2="12" />
                </svg>
                <span>{overlap.projectB.naam}</span>
              </div>
              <div className={styles.raakvlakOverlapMeta}>
                <span>{overlap.overlapPeriode.van} t/m {overlap.overlapPeriode.tot}</span>
                <span className={styles.raakvlakOverlapDagen}>{overlap.overlapPeriode.dagen} dagen</span>
              </div>
              {overlap.gedeeldeHinder.length > 0 && (
                <div className={styles.raakvlakOverlapHinder}>
                  {overlap.gedeeldeHinder.map(h => (
                    <span key={h.type} className={styles.raakvlakHinderTagSmall} style={{ borderColor: '#ef4444', color: '#ef4444' }}>
                      {h.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Communicatierisico's */}
      {communicatieRisicos.length > 0 && (
        <div className={styles.raakvlakRisicos}>
          <h4>Communicatierisico's</h4>
          {communicatieRisicos.map((risico, i) => (
            <div key={i} className={styles.raakvlakRisicoCard} style={{ borderLeftColor: NIVEAU_COLORS[risico.niveau] }}>
              <div className={styles.raakvlakRisicoHeader}>
                <span className={styles.raakvlakRisicoNiveau} style={{ backgroundColor: NIVEAU_COLORS[risico.niveau] }}>
                  {risico.niveau}
                </span>
                <span className={styles.raakvlakRisicoTitel}>{risico.titel}</span>
              </div>
              <p className={styles.raakvlakRisicoBeschrijving}>{risico.beschrijving}</p>
              <p className={styles.raakvlakRisicoAdvies}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#277CEA" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
                </svg>
                {risico.advies}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Projectenlijst */}
      {projecten.length > 0 && (
        <div className={styles.raakvlakProjecten}>
          <h4>Gedetecteerde projecten</h4>
          {projecten.slice(0, 8).map((project) => (
            <button
              key={project.id}
              className={styles.omgevingsListItem}
              onClick={() => project.lat && project.lng && onItemClick?.(project.lat, project.lng)}
            >
              <span className={styles.omgevingsStatusDot} style={{ backgroundColor: BRON_COLORS[project.bron] || '#9DA6A8' }} />
              <div className={styles.omgevingsListContent}>
                <span className={styles.omgevingsListTitle}>{project.naam}</span>
                <div className={styles.meldingListMeta}>
                  <span className={styles.meldingListBadge} style={{ color: BRON_COLORS[project.bron] || '#9DA6A8' }}>
                    {project.bron}
                  </span>
                  <span className={styles.meldingListBadge} style={{ color: '#666' }}>
                    {project.afstandMeter}m
                  </span>
                  <span className={styles.meldingListBadge} style={{ color: project.status === 'actief' ? '#ef4444' : project.status === 'gepland' ? '#f59e0b' : '#22c55e' }}>
                    {project.status}
                  </span>
                  {project.hinderTypes.slice(0, 2).map(h => (
                    <span key={h} className={styles.meldingListBadge} style={{ color: '#9DA6A8' }}>
                      {HINDER_TYPE_LABELS[h as HinderType] || h}
                    </span>
                  ))}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default RaakvlakprojectenSection;
